import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import Chip from '@mui/material/Chip';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ServiceIcon from '@mui/icons-material/Storage';
import ResourceIcon from '@mui/icons-material/AccountTree';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import { Button } from '../components/ui-kit';

import { getPermissions, getPermissionsByService, updatePermissionStatus, syncPermissions, deletePermission, bulkDeletePermissions, bulkUpdatePermissionStatus } from '../api/permissions';
import { usePermissions } from '../context/PermissionsContext';
import {
  ToggleButtonGroup,
  ToggleButton,
  Switch,
} from '../components/ui-kit';
import ConfirmationDialog from '../components/ConfirmationDialog';
import SortableTableHead from '../components/SortableTableHead';
import PermissionFiltersPanel from '../components/PermissionFiltersPanel';
import { PERMISSION_STATUS_OPTIONS } from '../utils/filterConstants';

// Calculate active filter count
export const getActiveFilterCount = (filters) => [
  filters.services?.length > 0 ? 1 : 0,
  filters.resources?.length > 0 ? 1 : 0,
  filters.actions?.length > 0 ? 1 : 0,
  filters.status !== 'all' ? 1 : 0
].reduce((a, b) => a + b, 0);

function PermissionsList() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const hasActions = hasPermission('auth-service', 'permission', 'delete');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return window.innerWidth < 768 ? 'accordion' : 'table';
  });
  const [permissionFilters, setPermissionFilters] = useState({
    services: [],
    resources: [],
    actions: [],
    status: 'all',
  });
  const { services: serviceFilter, resources: resourceFilter, actions: actionFilter, status: statusFilter } = permissionFilters;
  const [expandedServices, setExpandedServices] = useState(new Set());
  const [sortField, setSortField] = useState('service');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Fetch permissions grouped by service
  const { 
    data: permissionsByService = {}, 
    isLoading: isPermissionsLoading,
    error: permissionsError 
  } = useQuery(['permissions-by-service'], getPermissionsByService, {
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    onError: (error) => {
      console.error('Error fetching permissions by service:', error);
    }
  });

  // Fetch all permissions for table view with pagination and filters
  const { 
    data: allPermissions = {}, 
    isLoading: isAllPermissionsLoading 
  } = useQuery(['all-permissions', page, pageSize, serviceFilter, resourceFilter, actionFilter, statusFilter, sortField || '', sortDirection || 'asc'], () => 
    getPermissions({ 
      page, 
      limit: pageSize,
      service: serviceFilter.length > 0 ? serviceFilter : undefined,
      resource: resourceFilter.length > 0 ? resourceFilter : undefined,
      action: actionFilter.length > 0 ? actionFilter : undefined,
      isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      sortField: sortField || undefined,
      sortDirection: sortDirection || undefined
    }), {
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.error('Error fetching all permissions:', error);
    }
  });

  // Mutation for updating permission status
  const updateStatusMutation = useMutation({
    mutationFn: ({ permissionId, isActive }) => updatePermissionStatus(permissionId, isActive),
    onSuccess: (data, variables) => {
      // Обновляем кэш
      queryClient.invalidateQueries(['all-permissions']);
      queryClient.invalidateQueries(['permissions-by-service']);
      toast.success(`Permission status updated to ${variables.isActive ? 'Active' : 'Inactive'}`);
    },
    onError: (error) => {
      toast.error('Failed to update permission status');
    }
  });

  // Mutation for syncing permissions
  const syncPermissionsMutation = useMutation({
    mutationFn: syncPermissions,
    onSuccess: (data) => {
      // Обновляем кэш
      queryClient.invalidateQueries(['all-permissions']);
      queryClient.invalidateQueries(['permissions-by-service']);
      queryClient.invalidateQueries(['available-services']);
      queryClient.invalidateQueries(['available-resources']);
      queryClient.invalidateQueries(['available-actions']);
      
      toast.success(`Permissions synchronized successfully! Added: ${data.summary.added}, Deactivated: ${data.summary.deactivated}, Reactivated: ${data.summary.reactivated}`);
    },
    onError: (error) => {
      toast.error('Failed to sync permissions');
    }
  });

  // Track which permission is being updated
  const [updatingPermissionId, setUpdatingPermissionId] = useState(null);

  // Mutation for deleting a permission (soft delete)
  const deletePermissionMutation = useMutation({
    mutationFn: (permissionId) => deletePermission(permissionId),
    onSuccess: () => {
      toast.success('Permission deleted successfully');
      queryClient.invalidateQueries(['permissions-by-service']);
      queryClient.invalidateQueries(['all-permissions']);
    },
    onError: () => {
      toast.error('Failed to delete permission');
    }
  });

  // Bulk mutations
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => bulkDeletePermissions(ids),
    onSuccess: (data) => {
      toast.success(`Deleted ${data.deleted} permissions`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries(['permissions-by-service']);
      queryClient.invalidateQueries(['all-permissions']);
    },
    onError: () => toast.error('Failed bulk delete')
  });

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, isActive }) => bulkUpdatePermissionStatus({ ids, isActive }),
    onSuccess: (data) => {
      toast.success(`Updated ${data.modified ?? data.matched} permissions`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries(['permissions-by-service']);
      queryClient.invalidateQueries(['all-permissions']);
    },
    onError: () => toast.error('Failed bulk status update')
  });

  // Update view mode when window is resized
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      if (mobile) {
        setViewMode('accordion');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handler for switching view modes
  const handleViewModeChange = useCallback((event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  }, []);


  // Calculate active filter count
  const activeFilterCount = getActiveFilterCount(permissionFilters);

  // Handler to clear all filters
  const handleClearAllFilters = () => {
    setPermissionFilters({ services: [], resources: [], actions: [], status: 'all' });
    setPage(1);
    queryClient.invalidateQueries(['available-resources']);
    queryClient.invalidateQueries(['all-permissions']);
  };

  // Handle permission status change
  const handleStatusChange = useCallback((permissionId, newStatus) => {
    setUpdatingPermissionId(permissionId);
    updateStatusMutation.mutate(
      { permissionId, isActive: newStatus },
      {
        onSettled: () => {
          setUpdatingPermissionId(null);
        }
      }
    );
  }, [updateStatusMutation]);

  // Handler for SortableTableHead
  const handleSortRequest = useCallback((field) => {
    if (sortField === field) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') {
        setSortField('');
        setSortDirection('asc');
      } else setSortDirection('asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page when sorting
  }, [sortField, sortDirection]);

  // Toggle service expansion
  const toggleServiceExpansion = (service) => {
    const newExpanded = new Set(expandedServices);
    if (newExpanded.has(service)) {
      newExpanded.delete(service);
    } else {
      newExpanded.add(service);
    }
    setExpandedServices(newExpanded);
  };

  // Expand all services
  const expandAllServices = () => {
    const allServices = Object.keys(permissionsByService);
    setExpandedServices(new Set(allServices));
  };

  // Collapse all services
  const collapseAllServices = () => {
    setExpandedServices(new Set());
  };

  // Get action color for chips
  const getActionColor = (action) => {
    const colors = {
      create: 'success',
      read: 'primary',
      update: 'warning',
      delete: 'error',
      list: 'info',
      approve: 'success',
      reject: 'error',
      publish: 'success',
      unpublish: 'warning',
      execute: 'secondary',
      generate: 'info',
      export: 'secondary',
      assign: 'info',
      complete: 'success',
      moderate: 'warning',
      enroll: 'primary',
      take: 'secondary',
      award: 'success',
      send: 'info',
      process: 'secondary'
    };
    return colors[action] || 'default';
  };

  // Get action icon
  const getActionIcon = (action) => {
    const icons = {
      create: '➕',
      read: '👁️',
      update: '✏️',
      delete: '🗑️',
      list: '📋',
      approve: '✅',
      reject: '❌',
      publish: '📢',
      unpublish: '🔇',
      execute: '▶️',
      generate: '⚙️',
      export: '📤',
      assign: '👤',
      complete: '✅',
      moderate: '🛡️',
      enroll: '📚',
      take: '📝',
      award: '🏆',
      send: '📧',
      process: '⚙️'
    };
    return icons[action] || '🔒';
  };

  // Get permissions from server (filtering is now done on server)
  const getFilteredPermissions = useCallback(() => {
    if (!allPermissions || !allPermissions.permissions || !Array.isArray(allPermissions.permissions)) {
      return [];
    }
    
    return allPermissions.permissions;
  }, [allPermissions]);

  // Get total count for pagination from server
  const getTotalPermissions = () => {
    return allPermissions?.pagination?.total || 0;
  };


  // Handle page change for TablePagination
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage + 1); // TablePagination uses 0-based indexing
  }, []);

  // Handle page size change for TablePagination
  const handleChangeRowsPerPage = useCallback((event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page when changing page size
  }, []);

  // Selection helpers
  const getCurrentPagePermissions = useCallback(() => getFilteredPermissions(), [getFilteredPermissions]);
  const areAllSelected = useCallback(() => {
    const current = getCurrentPagePermissions();
    return current.length > 0 && current.every(p => selectedIds.has(p._id));
  }, [selectedIds, getCurrentPagePermissions]);

  const toggleSelectAll = useCallback(() => {
    const current = getCurrentPagePermissions();
    const next = new Set(selectedIds);
    if (current.length > 0 && current.every(p => next.has(p._id))) {
      current.forEach(p => next.delete(p._id));
    } else {
      current.forEach(p => next.add(p._id));
    }
    setSelectedIds(next);
  }, [selectedIds, getCurrentPagePermissions]);

  const toggleSelectOne = useCallback((id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  }, [selectedIds]);

  // Sort permissions
  const getSortedPermissions = (permissions) => {
    return permissions;
  };

  // Get sorted and paginated permissions for table view

  // Handle delete permission
  const handleDeletePermission = (permission) => {
    setSelectedPermission(permission);
    setDeleteDialogOpen(true);
  };

  // Confirm delete permission
  const confirmDeletePermission = async () => {
    if (!selectedPermission?._id) return;
    deletePermissionMutation.mutate(selectedPermission._id, {
      onSettled: () => {
        setDeleteDialogOpen(false);
        setSelectedPermission(null);
      }
    });
  };

  if (isPermissionsLoading || isAllPermissionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (permissionsError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Error loading permissions: {permissionsError.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      {/* Header: Title, Actions, and View Controls */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          mb: 3,
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="500">
          Permissions Management
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', sm: 'flex-end' },
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {hasPermission('auth-service', 'permission', 'sync') && (
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={() => syncPermissionsMutation.mutate()}
              disabled={syncPermissionsMutation.isLoading}
            >
              {syncPermissionsMutation.isLoading ? <CircularProgress size={20} /> : 'Sync Permissions'}
            </Button>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
            sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
          >
            <ToggleButton value="table" aria-label="table view">
              <TableRowsIcon />
            </ToggleButton>
            <ToggleButton value="accordion" aria-label="accordion view">
              <GridViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Permissions Info and Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Permissions ({allPermissions?.pagination?.total || 0})
          {allPermissions?.permissions && (
            <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 1 }}>
              (Page {page} of {allPermissions.pagination?.pages || 1})
            </Typography>
          )}
        </Typography>
        
        <Box display="flex" gap={1} alignItems="center">
          {viewMode === 'accordion' && (
            <>
              <Button size="small" onClick={expandAllServices}>
                Expand All
              </Button>
              <Button size="small" onClick={collapseAllServices}>
                Collapse All
              </Button>
            </>
          )}
          
          {/* Filter button */}
          <Tooltip title="Filter permissions">
            <IconButton
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              color={activeFilterCount > 0 ? "primary" : "default"}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                boxShadow: activeFilterCount > 0 ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
                '&:hover': {
                  backgroundColor: activeFilterCount > 0
                    ? 'rgba(25, 118, 210, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Badge badgeContent={activeFilterCount} color="primary">
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter panel that expands below */}
      <Box sx={{ mb: 4 }}>
        <PermissionFiltersPanel
          title="Filter Permissions"
          value={permissionFilters}
          onChange={setPermissionFilters}
          statusOptions={PERMISSION_STATUS_OPTIONS}
          isExpanded={isFiltersExpanded}
          onToggleExpand={() => setIsFiltersExpanded(!isFiltersExpanded)}
          onClearAll={handleClearAllFilters}
        />
      </Box>

      {/* Bulk Actions Bar */}
      <Collapse in={selectedIds.size > 0} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderLeft: 4,
            borderLeftColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={selectedIds.size}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Typography variant="body2" fontWeight={500}>
              {selectedIds.size === 1 ? 'permission selected' : 'permissions selected'}
            </Typography>
            <Tooltip title="Clear selection">
              <IconButton size="small" onClick={() => setSelectedIds(new Set())}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {hasPermission('auth-service', 'permission', 'update') && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => bulkStatusMutation.mutate({ ids: Array.from(selectedIds), isActive: true })}
                  disabled={bulkStatusMutation.isLoading}
                >
                  Set Active
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => bulkStatusMutation.mutate({ ids: Array.from(selectedIds), isActive: false })}
                  disabled={bulkStatusMutation.isLoading}
                >
                  Set Inactive
                </Button>
              </>
            )}
            {hasPermission('auth-service', 'permission', 'delete') && (
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                disabled={bulkDeleteMutation.isLoading}
              >
                {bulkDeleteMutation.isLoading ? 'Deleting...' : 'Delete Selected'}
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Permissions Display */}
      {viewMode === 'accordion' ? (
         // Accordion View - Grouped by Service
         <Box>
           {permissionsByService && typeof permissionsByService === 'object' && Object.keys(permissionsByService).length > 0 ? (
             Object.entries(permissionsByService).map(([service, resources]) => (
               <Accordion 
                 key={service}
                 expanded={expandedServices.has(service)}
                 onChange={() => toggleServiceExpansion(service)}
                 sx={{ mb: 1 }}
               >
                 <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                   <Box display="flex" alignItems="center" gap={2}>
                     <ServiceIcon color="primary" />
                     <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                       {service.replace('-service', '')} Service
                     </Typography>
                     <Chip 
                       label={Object.keys(resources).length} 
                       size="small" 
                       color="primary" 
                       variant="outlined"
                     />
                   </Box>
                 </AccordionSummary>
                 
                 <AccordionDetails>
                   <Box>
                     {Object.entries(resources).map(([resource, actions]) => (
                       <Accordion key={resource} sx={{ mb: 1 }}>
                         <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                           <Box display="flex" alignItems="center" gap={2}>
                             <ResourceIcon color="secondary" />
                             <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                               {resource.replace('-', ' ')}
                             </Typography>
                             <Chip 
                               label={actions.length} 
                               size="small" 
                               color="secondary" 
                               variant="outlined"
                             />
                           </Box>
                         </AccordionSummary>
                         
                         <AccordionDetails>
                           <Box display="flex" flexWrap="wrap" gap={1}>
                             {actions.map((action) => {
                               // Проверяем, что action - это строка
                               const actionName = typeof action === 'string' ? action : (action.name || action.action || String(action));
                               return (
                                 <Chip
                                   key={actionName}
                                   label={actionName}
                                   color={getActionColor(actionName)}
                                   variant="outlined"
                                   icon={<span>{getActionIcon(actionName)}</span>}
                                   size="small"
                                 />
                               );
                             })}
                           </Box>
                         </AccordionDetails>
                       </Accordion>
                     ))}
                   </Box>
                 </AccordionDetails>
               </Accordion>
             ))
           ) : (
             <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
               <Typography color="textSecondary">
                 {permissionsError ? 'Error loading permissions' : 'No permissions available'}
               </Typography>
             </Box>
           )}
         </Box>
      ) : (
        // Table View
        <>
          <TableContainer
          sx={{
            overflowX: 'auto', // Ensures horizontal scrolling
            boxShadow: (theme) => theme.shadows[1],
            borderRadius: 1,
            mb: 2
          }}
        >
          <Table
            sx={{
              minWidth: 900,
              tableLayout: 'fixed', // Ensures columns respect their width settings
              width: '100%'
            }}
            aria-label="permissions table"
          >
               <SortableTableHead
                leftComponent={
                  <Switch
                    checked={areAllSelected()}
                    onChange={toggleSelectAll}
                    color="primary"
                  />
                }
                columns={[
                   { field: 'service', headerName: 'Service', sortable: true },
                   { field: 'resource', headerName: 'Resource', sortable: true },
                   { field: 'action', headerName: 'Action', sortable: true },
                   { field: 'name', headerName: 'Name', sortable: true },
                   { field: 'isActive', headerName: 'Status', sortable: true },
                   { field: 'createdAt', headerName: 'Created', sortable: true },
                   { field: 'createdBy', headerName: 'Creator', sortable: true },
                   ...(hasActions ? [{ field: 'actions', headerName: 'Actions', sortable: false }] : []),
                 ]}
                 sortConfig={{ field: sortField || '', direction: sortDirection || 'asc' }}
                 handleSortRequest={handleSortRequest}
               />
               <TableBody>
                 {isAllPermissionsLoading ? (
                   <TableRow>
                     <TableCell colSpan={hasActions ? 9 : 8} align="center">
                       <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                         <CircularProgress size={24} />
                         <Typography variant="body2" sx={{ ml: 2 }}>
                           Loading permissions...
                         </Typography>
                       </Box>
                     </TableCell>
                   </TableRow>
                 ) : allPermissions?.permissions?.length === 0 ? (
                   <TableRow>
                     <TableCell colSpan={hasActions ? 9 : 8} align="center">
                       <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                         <Typography variant="body2" color="textSecondary">
                           No permissions found
                         </Typography>
                       </Box>
                     </TableCell>
                   </TableRow>
                 ) : (
                   getSortedPermissions(allPermissions.permissions).map((permission) => (
                     <TableRow
                       key={permission._id}
                       hover
                       sx={{
                         '&:last-child td, &:last-child th': { border: 0 },
                         '&:hover': {
                           backgroundColor: 'action.hover',
                         },
                         backgroundColor: permission.isActive
                           ? 'inherit'
                           : 'action.disabledBackground',
                       }}
                     >
                       <TableCell>
                         <Switch
                           checked={selectedIds.has(permission._id)}
                           onChange={() => toggleSelectOne(permission._id)}
                           color="primary"
                         />
                       </TableCell>
                       <TableCell>
                         <Box display="flex" alignItems="center" gap={1}>
                           <ServiceIcon color="primary" fontSize="small" />
                           <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                             {permission.service.replace('-service', '')}
                           </Typography>
                         </Box>
                       </TableCell>
                       <TableCell>
                         <Box display="flex" alignItems="center" gap={1}>
                           <ResourceIcon color="secondary" fontSize="small" />
                           <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                             {permission.resource.replace('-', ' ')}
                           </Typography>
                         </Box>
                       </TableCell>
                       <TableCell>
                         <Chip
                           label={typeof permission.action === 'string' ? permission.action : String(permission.action)}
                           color={getActionColor(permission.action)}
                           variant="outlined"
                           icon={<span>{getActionIcon(permission.action)}</span>}
                           size="small"
                         />
                       </TableCell>
                       <TableCell>
                         <Typography variant="body2">
                           {`${permission.service}.${permission.resource}.${permission.action}`}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <Box display="flex" alignItems="center" gap={1}>
                           <Switch
                             checked={permission.isActive}
                             onChange={(e) => handleStatusChange(permission._id, e.target.checked)}
                             disabled={
                               !hasPermission('auth-service', 'permission', 'update') ||
                               updatingPermissionId === permission._id
                             }
                             color="primary"
                           />
                           {updatingPermissionId === permission._id && (
                             <CircularProgress size={16} />
                           )}
                           <Typography variant="body2" color="textSecondary">
                             {permission.isActive ? 'Active' : 'Inactive'}
                           </Typography>
                         </Box>
                       </TableCell>
                       <TableCell>
                         <Typography variant="body2">
                           {permission?.metadata?.createdAt ? new Date(permission.metadata.createdAt).toLocaleString() : '—'}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <Typography variant="body2">
                           {permission?.metadata?.createdBy?.fullName || permission?.metadata?.createdBy?.email || permission?.metadata?.createdBy || '—'}
                         </Typography>
                       </TableCell>
                       {hasActions && (
                         <TableCell>
                           <Box display="flex" gap={1}>
                             <IconButton
                               size="small"
                               onClick={() => handleDeletePermission(permission)}
                               color="error"
                             >
                               <DeleteIcon />
                             </IconButton>
                           </Box>
                         </TableCell>
                       )}
                     </TableRow>
                   ))
                 )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={getTotalPermissions()}
            rowsPerPage={pageSize}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}


      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Permission"
        contentText={`Are you sure you want to delete the permission "${selectedPermission?.service}.${selectedPermission?.resource}.${selectedPermission?.action}"?`}
        confirmButtonText={deletePermissionMutation.isLoading ? 'Deleting...' : 'Delete'}
        onConfirm={confirmDeletePermission}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedPermission(null);
        }}
      />
    </Box>
  );
}

export default PermissionsList;
